import { Loader2 } from "lucide-react";

import type { MedicoFila } from "@/api/db/medico";
import { useMedicoUnignore } from "@/hooks/use-medico";
import { format } from "@/lib/format";
import { MedicoLabels } from "@/lib/labels/medico";

import { MetaLine, QueueRow, QueueSection } from "./queue-section";

function IgnoredRow({ transcript }: { transcript: MedicoFila["ignored"][number] }) {
	const unignore = useMedicoUnignore();

	const duration = MedicoLabels.duration(transcript.durationS);

	return (
		<QueueRow>
			<div
				className="flex flex-wrap items-center justify-between gap-2"
				data-component="ignored-row"
				data-transcript={transcript.transcriptId}
			>
				<div className="flex min-w-0 flex-col gap-1">
					<p className="min-w-0 truncate font-display text-sm font-medium text-foreground">
						{transcript.title}
					</p>

					<MetaLine>
						<span className="font-mono">{format.dateTime(transcript.recordedAt)}</span>

						{!!duration && <span className="font-mono">{duration}</span>}
					</MetaLine>
				</div>

				<button
					type="button"
					disabled={unignore.isPending}
					data-slot="restore"
					onClick={() => unignore.mutate({ transcriptId: transcript.transcriptId })}
					className="inline-flex items-center gap-1 text-xs font-medium text-primary transition-colors hover:text-primary-hover focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:opacity-50"
				>
					{unignore.isPending && <Loader2 className="size-3 animate-spin" />}
					{unignore.isPending && MedicoLabels.ignoredRestoring}
					{!unignore.isPending && MedicoLabels.ignoredRestore}
				</button>
			</div>
		</QueueRow>
	);
}

export function IgnoredSection({ transcripts }: { transcripts: MedicoFila["ignored"] }) {
	if (transcripts.length === 0) {
		return null;
	}

	return (
		<QueueSection
			title={MedicoLabels.ignoredTitle}
			subtitle={MedicoLabels.ignoredSubtitle(transcripts.length)}
		>
			{transcripts.map((transcript) => (
				<IgnoredRow key={transcript.transcriptId} transcript={transcript} />
			))}
		</QueueSection>
	);
}
